import dayjs, { Dayjs } from 'dayjs';
import ReactECharts from 'echarts-for-react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import trapImageApi from '../../apis/TrapImageApi';
import CustomContext from '../../contexts/CustomContext';
import { CustomAuth, Plot, TrapPlot, TrapImageStats, TrapMetricStats } from '../../models/Entities';
import alertService from '../../services/AlertService';
import dateService from '../../services/DateService';
import numericService from '../../services/NumericService';
import stringService from '../../services/StringService';
import styles from './ChartInsectsComponent.module.css';
import { Card } from 'antd/lib';
import { TimeRangePickerProps, DatePicker, Space, Select } from 'antd';
import trapMetricApi from '../../apis/TrapMetricApi';
import { StatsSecondaryMetric, statsSecondaryMetrics } from '../../models/Types';
import ConstantLabel from '../ConstantLabel/ConstantLabel';

/**
 * Returns the chart insects component.
 * @returns the chart insects component
 */
const ChartInsectsComponent: React.FC<Props> = (props) => {
    const { plot, trapPlot, secondaryMetricEnabled } = props;
    const { RangePicker } = DatePicker;

    /*** HOOKS ***/

    const intl = useIntl();
    const [trapImageStats, setTrapImageStats] = useState<TrapImageStats[]>([]);
    const [trapMetricStats, setTrapMetricStats] = useState<TrapMetricStats[]>([]);
    const [dataset, setDataset] = useState<(string | number | undefined)[][]>([]);
    const [series, setSeries] = useState<string[]>([]);
    const [dimensions, setDimensions] = useState<string[]>([]);
    const [startDate, setStartDate] = useState<Dayjs>(dayjs().subtract(14, 'days'));
    const [endDate, setEndDate] = useState<Dayjs>(dayjs());
    const [secondaryMetric, setSecondaryMetric] = useState<StatsSecondaryMetric>();
    const [displayMode, setDisplayMode] = useState<'accumulated' | 'variation'>('accumulated');
    const [, setLoading] = useState<'loading'>();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;

    /*** EFFECTS ***/

    // build chart data from trap image stats, trap metric stats and secondary metric
    const buildChartData = useCallback(
        (trapImageStats: TrapImageStats[], trapMetricStats: TrapMetricStats[]): any => {
            const trapPlots: TrapPlot[] = trapImageStats
                .map((tis) => {
                    tis.trapPlot.trap = Object.assign({}, tis.trapPlot.trap, {
                        uuid: tis.uuid,
                        shortUuid: tis.shortUuid,
                        plague: tis.plague
                    });
                    return tis;
                })
                .map((tis) => tis.trapPlot)
                .filter((value, index, self) => index === self.findIndex((t) => t.id === value.id))
                .sort((a, b) => stringService.sort(a.trap?.uuid, b.trap?.uuid));
            const series: string[] = trapPlots.map((te) => `${intl.formatMessage({ id: `plague.type.${te.trap?.plague}` })} (${te.trap?.shortUuid})`);
            const dimensions: string[] = ['timestamp', ...series];
            if (secondaryMetric) {
                dimensions.push(secondaryMetric);
            }

            const dates: Dayjs[] = trapImageStats
                .map((tis) => tis.date)
                .map((d) => d.startOf('day'))
                .filter((value, index, self) => index === self.findIndex((t) => t.isSame(value, 'day')))
                .sort(dateService.sort);

            // build raw accumulated insects per date and trap plot
            const accumulatedInsects: (string | number | undefined)[][] = dates.map((date) => {
                const values: (number | undefined)[] = trapPlots.flatMap((trapPlot) =>
                    trapImageStats
                        .filter((tis) => tis.trapPlot.id === trapPlot.id && tis.date.isSame(date, 'day'))
                        .map((tis) => tis.maxInsects)
                        .find((maxInsects) => maxInsects !== undefined)
                );
                return [date.toISOString(), ...values];
            });

            // calculate variation (day-over-day difference) if needed
            const insectRows: (string | number | undefined)[][] =
                displayMode === 'variation'
                    ? accumulatedInsects.map((row, rowIndex) => {
                          if (rowIndex === 0) {
                              // First day: no previous day, use undefined for all series
                              return [row[0], ...trapPlots.map(() => undefined)];
                          }
                          const prevRow = accumulatedInsects[rowIndex - 1];
                          return [
                              row[0],
                              ...trapPlots.map((_, i) => {
                                  const curr = row[i + 1] as number | undefined;
                                  const prev = prevRow[i + 1] as number | undefined;
                                  return curr !== undefined && prev !== undefined ? curr - prev : undefined;
                              })
                          ];
                      })
                    : accumulatedInsects;

            // append secondary metric column
            const dataset: (string | number | undefined)[][] = insectRows.map((row, rowIndex) => {
                const date = dates[rowIndex];
                const result = [...row];

                if (secondaryMetric) {
                    const secondaryMetricValues = trapMetricStats
                        .filter((tms) => tms.date.isSame(date, 'day'))
                        .map((tms) => {
                            let metricValue: number | undefined;
                            switch (secondaryMetric) {
                                case 'AVG_TEMPERATURE':
                                    metricValue = tms.avgTemperature;
                                    break;
                                case 'AVG_HUMIDITY':
                                    metricValue = tms.avgHumidity;
                                    break;
                                case 'AVG_WIND':
                                    metricValue = numericService.msToKmh(tms.avgWindSpeed);
                                    break;
                                default:
                                    break;
                            }
                            return metricValue;
                        })
                        .filter((w) => w !== undefined) as number[];

                    const secondaryMetricValue =
                        secondaryMetricValues.length > 0 ? secondaryMetricValues.reduce((a, b) => a + b, 0) / secondaryMetricValues.length : undefined;
                    result.push(secondaryMetricValue);
                }

                return result;
            });

            return { series, dimensions, dataset };
        },
        [secondaryMetric, displayMode, intl]
    );

    // load trap image stats and trap metric stats
    useEffect(() => {
        const init = async () => {
            try {
                setLoading('loading');
                let trapImageStats: TrapImageStats[] = [];
                let trapMetricStats: TrapMetricStats[] = [];

                if (auth.companyId && trapPlot && trapPlot.id) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId, undefined, trapPlot.id);
                    trapMetricStats = await trapMetricApi.listStats(startDate, endDate, 'DAY', auth.companyId, undefined, trapPlot.id);
                } else if (auth.companyId && plot && plot.id) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId, plot.id);
                    trapMetricStats = await trapMetricApi.listStats(startDate, endDate, 'DAY', auth.companyId, plot.id);
                } else if (auth.companyId && !plot) {
                    trapImageStats = await trapImageApi.listStats(startDate, endDate, 'DAY', auth.companyId);
                }

                setTrapImageStats(trapImageStats);
                setTrapMetricStats(trapMetricStats);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };

        init();
    }, [intl, auth.companyId, plot, trapPlot, startDate, endDate]);

    // build chart data
    useEffect(() => {
        const init = async () => {
            try {
                const { dimensions, series, dataset } = buildChartData(trapImageStats, trapMetricStats);
                setSeries(series);
                setDimensions(dimensions);
                setDataset(dataset);
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };

        init();
    }, [trapImageStats, trapMetricStats, secondaryMetric, displayMode, buildChartData, intl]);

    /*** METHODS ***/

    const onRangeChange = (dates: null | (Dayjs | null)[], dateStrings: string[]) => {
        if (dates) {
            const startDate = dateStrings[0];
            const endDate = dateStrings[1];

            setStartDate(dayjs(startDate));
            setEndDate(dayjs(endDate));
        }
    };

    /*** VISUAL ***/

    const statsSecondaryMetricOptions = statsSecondaryMetrics.map((p) => (
        <Select.Option key={p} value={p}>
            <ConstantLabel value={p} prefix="statsSecondaryMetric.type." />
        </Select.Option>
    ));

    const rangePresets: TimeRangePickerProps['presets'] = [
        { label: <FormattedMessage id={`dashboard.range.7.days`} />, value: [dayjs().add(-7, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.14.days`} />, value: [dayjs().add(-14, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.30.days`} />, value: [dayjs().add(-30, 'd'), dayjs()] },
        { label: <FormattedMessage id={`dashboard.range.90.days`} />, value: [dayjs().add(-90, 'd'), dayjs()] }
    ];

    const isVariation = displayMode === 'variation';

    return (
        <div className={styles.container}>
            <Card
                bordered={false}
                className={styles.card}
                title={<FormattedMessage id="dashboard.chart.insects.title" />}
                extra={
                    <Space>
                        <Select value={displayMode} size="small" onChange={(value) => setDisplayMode(value)}>
                            <Select.Option value="accumulated">
                                <FormattedMessage id="dashboard.chart.insects.displayMode.accumulated" />
                            </Select.Option>
                            <Select.Option value="variation">
                                <FormattedMessage id="dashboard.chart.insects.displayMode.variation" />
                            </Select.Option>
                        </Select>

                        {secondaryMetricEnabled && (
                            <Select
                                value={secondaryMetric}
                                placeholder={<FormattedMessage id="dashboard.chart.insects.secondaryMetric" />}
                                size="small"
                                onChange={(value) => setSecondaryMetric(value)}
                                allowClear
                            >
                                {statsSecondaryMetricOptions}
                            </Select>
                        )}
                        <RangePicker
                            presets={rangePresets}
                            size="small"
                            onChange={onRangeChange}
                            style={{ maxWidth: 220 }}
                            defaultValue={[startDate, endDate]}
                            allowClear={false}
                        />
                    </Space>
                }
            >
                <ReactECharts
                    option={{
                        legend: {
                            bottom: 0,
                            formatter: (name: string) => {
                                let formattedName: string = name;
                                if (statsSecondaryMetrics.some((ssm) => ssm === name)) {
                                    formattedName = intl.formatMessage({ id: `statsSecondaryMetric.type.${name}` });
                                }

                                return formattedName;
                            }
                        },
                        grid: {
                            top: 10
                        },
                        dataset: {
                            source: dataset,
                            dimensions
                        },
                        xAxis: { type: 'time' },
                        yAxis: secondaryMetric
                            ? [
                                  {
                                      name: intl.formatMessage({ id: 'chart.insects' })
                                  },
                                  {
                                      name: intl.formatMessage({
                                          id: `statsSecondaryMetric.type.${secondaryMetric}`
                                      }),
                                      position: 'right'
                                  }
                              ]
                            : [
                                  {
                                      name: intl.formatMessage({ id: 'chart.insects' })
                                  }
                              ],
                        series: [
                            ...series.map((s) => ({
                                name: s,
                                type: isVariation ? 'bar' : 'line',
                                ...(isVariation
                                    ? {
                                          barMaxWidth: 20
                                      }
                                    : {
                                          symbol: 'emptyCircle',
                                          connectNulls: true,
                                          smooth: true
                                      }),
                                encode: {
                                    x: 'timestamp',
                                    y: s
                                }
                            })),
                            ...(secondaryMetric
                                ? [
                                      {
                                          name: secondaryMetric,
                                          type: 'line',
                                          smooth: true,
                                          yAxisIndex: 1,
                                          lineStyle: {
                                              type: 'dashed',
                                              width: 1,
                                              color: '#000'
                                          },
                                          itemStyle: {
                                              color: '#000'
                                          },
                                          symbol: 'none',
                                          encode: {
                                              x: 'timestamp',
                                              y: secondaryMetric
                                          }
                                      }
                                  ]
                                : [])
                        ],
                        tooltip: {
                            trigger: 'axis',
                            formatter: (params: any) => {
                                const parameters = Array.isArray(params) ? params : [params];
                                const sortedParameters = parameters.sort((a, b) => {
                                    if (a.seriesName === secondaryMetric) return -1;
                                    if (b.seriesName === secondaryMetric) return 1;
                                    return 0;
                                });
                                const date = dayjs(sortedParameters[0]?.axisValue).format('DD/MM/YYYY');

                                // build series line in tooltip
                                const seriesRows = sortedParameters.map((p) => {
                                    const seriesName = p.seriesName;
                                    const dimensionNames = p.dimensionNames || [];
                                    const index = dimensionNames.indexOf(seriesName);
                                    const row = p.data;
                                    const value = index >= 0 ? row[index] : p.value;
                                    const isSecondaryMetric = statsSecondaryMetrics.some((ssm) => ssm === seriesName);

                                    // get label and units
                                    let label: string = seriesName;
                                    let units: string = intl.formatMessage({ id: 'chart.insects' });
                                    if (isSecondaryMetric) {
                                        label = intl.formatMessage({ id: `statsSecondaryMetric.type.${seriesName}` });
                                        units = intl.formatMessage({ id: `statsSecondaryMetric.type.${seriesName}.units` });
                                    }

                                    // color the variation value
                                    const sign = isVariation && !isSecondaryMetric && value !== undefined && value > 0 ? '+' : '';

                                    return `
                                        <tr style="line-height:1.1;">
                                            <td>${p.marker}</td>
                                            <td style="padding-right:24px;">${label}</td>
                                            <td style="font-weight:bold; text-align:right;">
                                                ${
                                                    value !== undefined
                                                        ? `${sign}${intl.formatNumber(value, {
                                                              minimumFractionDigits: isSecondaryMetric ? 2 : 0,
                                                              maximumFractionDigits: isSecondaryMetric ? 2 : 0
                                                          })} ${units}`
                                                        : ''
                                                }
                                            </td>
                                        </tr>
                                    `;
                                });

                                return `
                                    <div>
                                        <div style="margin-bottom:4px;">
                                            ${date}
                                        </div>
                                        <table>
                                            ${seriesRows.join('')}
                                        </table>
                                    </div>
                                `;
                            },
                            axisPointer: {
                                label: {
                                    formatter: function (params: any) {
                                        return dayjs(params.value).format('DD/MM/YYYY');
                                    }
                                }
                            }
                        }
                    }}
                    notMerge={true}
                />
            </Card>
        </div>
    );
};
export default ChartInsectsComponent;

interface Props {
    plot?: Plot;
    trapPlot?: TrapPlot;
    secondaryMetricEnabled?: boolean;
}
